import { useCallback, useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { ApiErrors, PixType } from '../../enums';
import { pixKeysApi } from '../../services/pixKeysApi';
import { unformatValue } from '../../utils/pixFormatters';
const getErrorCode = (error) => {
    const errorObj = error;
    return errorObj?.result?.error ?? null;
};
const ERROR_MESSAGE_MAP = {
    INVALID_PIX_KEY: 'pix-keys.validation.invalid-key',
    PIX_KEY_ALREADY_EXISTS: 'pix-keys.validation.key-already-exists',
    [ApiErrors.INVALID_PIX_KEY_CREATION_CODE]: 'pix-keys.confirmation.validation-error',
    DEFAULT: 'pix-keys.validation.error',
};
export function useValidatePixKey({ key, keyType, protocolId, onSuccess, onError, }) {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag(undefined, { autoLoad: false });
    const isBaasV2 = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const [verificationCode, setVerificationCode] = useState('');
    const [error, setError] = useState(null);
    const [validatePixKey, { isLoading: isV1Loading }] = pixKeysApi.useValidatePixKeyMutation();
    const [validatePixKeyV2, { isLoading: isV2Loading }] = pixKeysApi.useValidatePixKeyV2Mutation();
    const isValidationLoading = isV1Loading || isV2Loading;
    const handleVerificationCodeChange = useCallback((code) => {
        setVerificationCode(code);
        setError(null);
    }, []);
    const handleValidateCode = useCallback(async () => {
        if (!verificationCode || verificationCode.length !== 6) {
            const errorMessage = t('pix-keys.confirmation.code-required');
            setError(errorMessage);
            onError(errorMessage);
            return;
        }
        try {
            let formattedKey = key;
            if (keyType === PixType.PHONE) {
                formattedKey = key.startsWith('+55') ? `+55${unformatValue(key.substring(3))}` : `+55${unformatValue(key)}`;
            }
            const validateFn = isBaasV2 ? validatePixKeyV2 : validatePixKey;
            await validateFn({
                key: formattedKey,
                request: {
                    verificationCode,
                    protocolId,
                    ...(isBaasV2 && { type: keyType }),
                },
            }).unwrap();
            onSuccess();
        }
        catch (error) {
            const errorCode = getErrorCode(error);
            const errorMessage = errorCode ? t(ERROR_MESSAGE_MAP[errorCode]) : t(ERROR_MESSAGE_MAP.DEFAULT);
            setError(errorMessage);
            if (errorCode !== ApiErrors.INVALID_PIX_KEY_CREATION_CODE) {
                onError(errorMessage);
            }
        }
    }, [verificationCode, isBaasV2, validatePixKeyV2, validatePixKey, key, keyType, protocolId, onSuccess, onError, t]);
    return {
        verificationCode,
        isValidationLoading,
        error,
        handleVerificationCodeChange,
        handleValidateCode,
    };
}
